import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

import { withTranslation } from "react-i18next";

import Snackbar from "@plannel/components/Snackbar";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

export const ADJUST_TYPE = { ADJUST: "adjust", SPLIT: "split" };

// old AdjustQtyPopup.jsx의 inputBoxClass="adjustQtyEditable"(배경 rgb(255,242,204)) 대응 —
// "이 칸만 입력 가능"을 나타내는 노란 배경. CSS 클래스 대신 sx로 옮긴다(신규 scss 파일 없음).
const EDITABLE_INPUT_SX = { "& .MuiInputBase-root": { backgroundColor: "rgb(255, 242, 204)" } };

/**
 * 작업 수량 보정/분할 팝업 — old `adjustmentgrid3/AdjustQtyPopup.jsx` 대응.
 * old의 `react-hook-form`(new 미사용)은 화면 state로 번역했고, old가 팝업 안에서 직접 띄웠던
 * 확인 프롬프트(FP_MSG_ADJUST)는 호출부(화면)의 확인 Dialog로 옮겼다 — "확인 Dialog는 화면이
 * 하나만 갖는다"는 new 관례(fp-simulation-screen-guide.md §A-8/§B-10)에 맞춘 것.
 *
 * props: type(ADJUST_TYPE), open, activity(대상 작업), onClose(), onConfirm(data)
 *  - ADJUST → onConfirm({ adjustQty })
 *  - SPLIT  → onConfirm({ splitQty, remainingQty })
 */
const AdjustQtyPopup = ({ t, type, open, activity, onClose, onConfirm }) => {
  const isSplit = type === ADJUST_TYPE.SPLIT;

  const [inputQty, setInputQty] = useState("");
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const originQty = activity?.qty ?? 0;
  // old: splitQty 입력 시 remainingQty = originQty - splitQty (같으면 0). 파생값이라 state로 두지 않는다.
  const remainingQty = inputQty === "" ? 0 : Number(originQty) - Number(inputQty);

  useEffect(() => {
    if (!open) return;
    setInputQty("");
  }, [open, activity]);

  // old `InputField min={0} max={watch('originQty')}` 대응 — **입력 단계에서 범위를 강제한다.**
  // `TextField type="number"`의 min/max는 스피너만 제한하고 타이핑·붙여넣기는 그대로 통과하므로
  // 여기서 직접 거른다. 범위를 벗어난 입력은 **무시**해서 직전 값이 남는다(입력칸이 비지 않는다).
  // 분할 상한은 원본 수량 — 넘으면 "보정 수량"이 음수가 되어 뜻이 없는 값이 된다(2026-08-25 사용자 지적).
  const handleQtyChange = (event) => {
    const raw = event.target.value;
    if (raw === "") {
      setInputQty("");
      return;
    }

    const num = Number(raw);
    if (Number.isNaN(num) || num < 0) return;
    if (isSplit && num > Number(originQty)) return;

    setInputQty(raw);
  };

  const handleConfirm = () => {
    // old rules={{ validate: { required: value => !!value } }} + validationText FP_MSG_FIELD_REQUIRED.
    // 0도 old에서 falsy로 걸러졌으므로(!!0 === false) 동일하게 막는다.
    // 상한(분할 수량 ≤ 원본 수량)은 handleQtyChange가 입력 시점에 막으므로 여기서 다시 보지 않는다 —
    // old도 확인 시점 검사 없이 입력 제한만 뒀다.
    if (inputQty === "" || Number(inputQty) === 0 || Number.isNaN(Number(inputQty))) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_FIELD_REQUIRED") });
      return;
    }

    onConfirm(isSplit
      ? { splitQty: Number(inputQty), remainingQty: Number(remainingQty) }
      : { adjustQty: Number(inputQty) });
    onClose();
  };

  return (
    <MuiDialog open={!!open} onClose={onClose} maxWidth={false}>
      <DialogTitle>{t(isSplit ? "FP_SPLIT_ACTIVITY_QTY" : "FP_ADJUST_ACTIVITY_QTY")}</DialogTitle>
      <DialogContent>
        {/* 필드를 한 줄에 나란히 두고 **팝업 폭은 내용이 정하게** 한다(width 고정 + wrap 조합이면
            필드가 아래로 밀리고 그만큼 오른쪽이 빈다 — 2026-08-25 수정).
            결과 폭은 보정 272 + 분할 414(각 필드 130 + gap 12)로, old popupWidth(300/440)와 비슷하다. */}
        <Box sx={{ display: "flex", gap: 1.5, pt: 1 }}>
          <TextField
            size="small" type="number" label={t("FP_ACTIVITY_QTY")}
            value={originQty} InputProps={{ readOnly: true }} sx={{ width: 130 }}
          />
          {isSplit && (
            <TextField
              size="small" type="number" label={t("FP_ADJUST_QTY")}
              value={remainingQty} InputProps={{ readOnly: true }} sx={{ width: 130 }}
            />
          )}
          <TextField
            size="small" type="number" autoFocus
            label={t(isSplit ? "FP_SPLIT_QTY" : "FP_ADJUST_QTY")}
            value={inputQty}
            onChange={handleQtyChange}
            // 스피너·브라우저 검증용. 실제 강제는 handleQtyChange가 한다.
            inputProps={{ min: 0, ...(isSplit ? { max: originQty } : {}) }}
            sx={{ width: 130, ...EDITABLE_INPUT_SX }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button variant="contained" onClick={handleConfirm}>{t("confirm")}</Button>
      </DialogActions>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </MuiDialog>
  );
};

export default withTranslation()(AdjustQtyPopup);
